import React, { useState, useRef, useCallback } from "react";
import { Dropdown, DropdownItem } from "../Dropdown";
import "./style.scss";

interface PerformanceSummaryProps {
  title: string;
  subtitle: string;
  result: number | string;
  dropdownItems?: DropdownItem[];
  onSelectItem?: (item: DropdownItem) => void;
  selectedItem?: DropdownItem;
  disableParentSelecting?: boolean;
}

export const PerformanceSummary: React.FC<PerformanceSummaryProps> = ({
  title,
  subtitle,
  result,
  dropdownItems = [],
  onSelectItem,
  selectedItem,
  disableParentSelecting = false,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  const findItemPath = useCallback(
    (
      items: DropdownItem[] = [],
      path: DropdownItem[] = []
    ): DropdownItem[] | null => {
      for (const item of items) {
        const currentPath = [...path, item];
        if (item.id === selectedItem?.id) {
          return currentPath;
        }
        if (item.children) {
          const found = findItemPath(item.children, currentPath);
          if (found) return found;
        }
      }
      return null;
    },
    [selectedItem]
  );

  const selectedItemPath = findItemPath(dropdownItems) || [];
  const newSelectedItem =
    selectedItemPath.length > 0
      ? {
          item: selectedItemPath[selectedItemPath.length - 1],
          breadcrumb: selectedItemPath.map(i => i.title).join(" > "),
        }
      : { item: { title: subtitle, id: "" }, breadcrumb: subtitle };

  const handleItemSelect = useCallback(
    (item: DropdownItem) => {
      onSelectItem?.(item);
      setIsDropdownOpen(false);
    },
    [onSelectItem]
  );

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const hasDropdown = dropdownItems.length > 0;

  return (
    <div className={`performance-summary ${isDropdownOpen ? "active" : ""}`}>
      <div className="top">
        <div className="title">{title}</div>
        <div className="result">{result}%</div>
        {hasDropdown && (
          <div
            className="dropdown-trigger"
            onClick={toggleDropdown}
            ref={triggerRef}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={isDropdownOpen ? "active" : ""}
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </div>
        )}
      </div>
      <div className="subtitle" title={newSelectedItem.breadcrumb}>
        {newSelectedItem.breadcrumb}
      </div>
      {hasDropdown && (
        <Dropdown
          items={dropdownItems}
          isOpen={isDropdownOpen}
          onClose={() => setIsDropdownOpen(false)}
          triggerRef={triggerRef}
          onSelect={handleItemSelect}
          selectedItem={selectedItem}
          disableParentSelecting={disableParentSelecting}
        />
      )}
    </div>
  );
};
